import React, { useMemo, useState } from "react";
import BootstrapModal from "../shared/BootstrapModal";

const getIso2 = (country) =>
  String(country?.iso2 || country?.value || "").toUpperCase();

const normalizeCoupon = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

const ToyyibpayCheckout = ({
  selectedGateway,
  price_slug,
  fullName,
  email,
  selectedCountry,
  selectedState,
  postalCode,
  dialingCode,
  contactNumber,
  agreementAccepted,
  couponCode,
  freeTrialNoCard,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appErrors, setAppErrors] = useState([]);

  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionErrors, setPermissionErrors] = useState([]);

  const PERMISSION_ERROR_TYPES = useMemo(
    () => ({
      PRICE_PLAN: "ERR_PRICE_PLAN_PERMISSION",
      PRICE_PLAN_GENERAL: "ERR_PRICE_PLAN_GENERAL",
      COUPON: "ERR_COUPON_PERMISSION",
    }),
    [],
  );

  const closePermissionModal = () => {
    setShowPermissionModal(false);
  };

  const permissionGroups = useMemo(() => {
    const groups = { pricePlan: [], coupon: [] };

    for (const e of permissionErrors) {
      const code = String(e?.code || "");

      if (
        code === PERMISSION_ERROR_TYPES.PRICE_PLAN ||
        code === PERMISSION_ERROR_TYPES.PRICE_PLAN_GENERAL
      ) {
        groups.pricePlan.push(e);
      }

      if (code === PERMISSION_ERROR_TYPES.COUPON) {
        groups.coupon.push(e);
      }
    }

    return groups;
  }, [permissionErrors, PERMISSION_ERROR_TYPES]);

  const permissionModalType = useMemo(() => {
    if (permissionGroups.pricePlan.length > 0) return "PRICE_PLAN";
    if (permissionGroups.coupon.length > 0) return "COUPON";
    return null;
  }, [permissionGroups]);

  const permissionModalConfig = useMemo(() => {
    const isPricePlan = permissionModalType === "PRICE_PLAN";
    const isCoupon = permissionModalType === "COUPON";

    const cfg = {
      title: "",
      onClose: closePermissionModal,
      footer: (
        <button
          type='button'
          className='btn btn-lg btn-success'
          onClick={closePermissionModal}
        >
          Okay
        </button>
      ),
      intro: null,
    };

    if (isCoupon) {
      cfg.intro = (
        <p className='mb-3'>
          This coupon code cannot be applied to the following items due to
          eligibility requirements.
        </p>
      );
      return cfg;
    }

    if (isPricePlan) {
      cfg.onClose = undefined;
      cfg.footer = null;
      cfg.intro = <></>;
      return cfg;
    }

    return cfg;
  }, [permissionModalType]);

  const validateBeforeSubmit = () => {
    const errors = [];

    if (!String(fullName || "").trim()) errors.push("Full name is required.");
    if (!String(email || "").trim()) errors.push("Email is required.");
    if (!getIso2(selectedCountry)) errors.push("Country / region is required.");
    if (!String(contactNumber || "").trim())
      errors.push("Contact number is required.");
    if (!agreementAccepted) {
      errors.push("You must agree to the terms before continuing.");
    }
    if (!selectedGateway?._id) errors.push("Payment gateway is missing.");

    return errors;
  };

  const submitHandler = async () => {
    const errors = validateBeforeSubmit();

    if (errors.length) {
      setAppErrors(errors);
      return;
    }

    setAppErrors([]);
    setPermissionErrors([]);
    setShowPermissionModal(false);
    setIsSubmitting(true);

    try {
      const endpoint_url = `${process.env.REACT_APP_API_ENDPOINT}/api/v1/stores/price-plans/${price_slug}/payment-processing`;

      const url = new URL(endpoint_url);

      if (couponCode) url.searchParams.set("coupon_code", couponCode);

      const body = {
        payment_account_id: selectedGateway._id,

        name: String(fullName || "").trim(),
        email: String(email || "").trim(),

        country: selectedCountry?.name || "",
        country_iso2: getIso2(selectedCountry),
        state: String(selectedState?.name || "").trim(),
        postal_code: String(postalCode || "")
          .trim()
          .toUpperCase(),

        phone_code: String(dialingCode || "").replace(/\s+/g, ""),
        phone_number: String(contactNumber || "").trim(),

        authorized: agreementAccepted,
        authorized_at: new Date().toISOString(),
        user_agent: window.navigator.userAgent,
      };

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const msg =
          data?.errors?.[0]?.message ||
          data?.error?.message ||
          "Failed to create hosted payment page.";

        const err = new Error(msg);
        err.htmlMessage = msg;
        err.errors = Array.isArray(data?.errors) ? data.errors : [];
        throw err;
      }

      const payload =
        data?.payloads?.checkout_intent ||
        data?.checkout_intent ||
        data?.paymentTokenInfo ||
        data?.checkoutIntentInfo ||
        null;

      const paymentUrl = payload?.payment_url || null;

      if (!paymentUrl) {
        throw new Error("Missing ToyyibPay hosted payment URL.");
      }

      window.location.href = paymentUrl;
    } catch (error) {
      const all = Array.isArray(error?.errors) ? error.errors : [];

      const permissionList = all.filter((e) => {
        const code = String(e?.code || "");
        return (
          code === PERMISSION_ERROR_TYPES.PRICE_PLAN ||
          code === PERMISSION_ERROR_TYPES.PRICE_PLAN_GENERAL ||
          code === PERMISSION_ERROR_TYPES.COUPON
        );
      });

      if (permissionList.length > 0) {
        setPermissionErrors(permissionList);
        setShowPermissionModal(true);
        setIsSubmitting(false);
        return;
      }

      const nestedErrors = Array.isArray(error?.errors)
        ? error.errors.map((item) => item?.message).filter(Boolean)
        : [];

      if (nestedErrors.length > 0) {
        setAppErrors(nestedErrors);
      } else {
        setAppErrors([error?.message || "Something went wrong."]);
      }

      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* {freeTrialNoCard && (
        <div className='alert alert-success py-2'>
          No card is required for this free trial.
        </div>
      )} */}

      <button
        type='button'
        className='btn btn-lg fw-bold rounded-pill text-white w-100'
        style={{ backgroundColor: "#3CAD66" }}
        onClick={submitHandler}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Redirecting..." : "Complete purchase"}
      </button>

      {Array.isArray(appErrors) && appErrors.length > 0 && (
        <div className='piranha-error-box mt-3'>
          <div className='piranha-error-title'>Please fix the following:</div>
          <ul className='m-0 ps-3'>
            {appErrors.map((msg, idx) => (
              <li
                key={idx}
                dangerouslySetInnerHTML={{ __html: String(msg || "") }}
              />
            ))}
          </ul>
        </div>
      )}

      <BootstrapModal
        show={showPermissionModal}
        title={permissionModalConfig.title}
        onClose={permissionModalConfig.onClose}
        staticBackdrop
        keyboard={false}
        dialogClassName='modal-lg modal-dialog-centered'
        footer={permissionModalConfig.footer}
      >
        {permissionModalConfig.intro}

        {permissionModalType === "PRICE_PLAN" && (
          <div className='vstack gap-3'>
            {permissionGroups.pricePlan.map((e, idx) => (
              <div key={`price-${e?.code}-${e?.price_id || idx}`}>
                {e?.message && (
                  <div
                    dangerouslySetInnerHTML={{ __html: String(e.message) }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {permissionModalType === "COUPON" && (
          <div className='list-group'>
            {permissionGroups.coupon.map((e, idx) => (
              <div
                key={`coupon-${e?.code}-${e?.price_id || idx}`}
                className='list-group-item'
              >
                <div className='fw-bold'>
                  {idx + 1}. {e?.price_name || "Item"}
                </div>

                {e?.message && (
                  <div
                    className='text-muted mt-2'
                    dangerouslySetInnerHTML={{ __html: String(e.message) }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </BootstrapModal>
    </>
  );
};

export default ToyyibpayCheckout;
